import React from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import CustomInput from '@/components/common/CustomInput';
import { useMembersChangePasswordMutation } from '@/store/api/commonApi';
import { useRoleUserChangePasswordMutation } from '@/store/api/rolesApi';
import { changePasswordSchema, changePasswordWithCodeSchema } from '@/utils/validation';
import { showToastMessage } from '@/utils';

interface ChangePasswordProps {
  selectedUser: {
    id: string;
    username: string;
  } | null;
  onCancel: () => void;
  isRoleUser?: boolean;
}

type FormValues = z.infer<typeof changePasswordWithCodeSchema>;

const ChangePassword: React.FC<ChangePasswordProps> = ({
  selectedUser,
  onCancel,
  isRoleUser = false,
}) => {
  const [membersChangePassword, { isLoading }] = useMembersChangePasswordMutation();
  const [roleUserChangePassword, { isLoading: isRoleUserLoading }] =
    useRoleUserChangePasswordMutation();

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormValues>({
    resolver: zodResolver(isRoleUser ? changePasswordWithCodeSchema : changePasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
      transactionCode: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      if (isRoleUser) {
        await roleUserChangePassword({
          id: selectedUser?.id!,
          newPassword: values.newPassword,
          transactionCode: values.transactionCode!,
        }).unwrap();
      } else {
        await membersChangePassword({
          id: selectedUser?.id!,
          newPassword: values.newPassword,
        }).unwrap();
      }

      showToastMessage('success', 'Password Successfully Changed!');
      onCancel();
    } catch (err) {
      let msg = 'Failed to change password';
      if (err && typeof err === 'object' && 'data' in err) {
        msg = (err as any)?.data?.message || msg;
      }
      showToastMessage('error', msg);
    }
  };

  const passwordFields = [
    {
      name: 'newPassword',
      label: 'New Password',
      placeholder: 'Enter new password',
      type: 'password',
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password',
      placeholder: 'Confirm new password',
      type: 'password',
    },
    ...(isRoleUser
      ? [
          {
            name: 'transactionCode',
            label: 'Transaction Code',
            placeholder: 'Enter transaction code',
            type: 'text',
          },
        ]
      : []),
  ];
  const isSubmitting = isLoading || isRoleUserLoading;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {passwordFields.map((field) => (
        <CustomInput
          key={field.name}
          name={field.name as keyof FormValues}
          label={field.label}
          placeholder={field.placeholder}
          type={field.type}
          control={control}
          error={errors[field.name as keyof FormValues]}
          className="w-full"
          inputClassName="bg-white/10 w-full"
          labelClassName="font-medium text-base"
        />
      ))}

      <div className="flex justify-end gap-4 border-t border-green3 pt-2">
        <button
          type="button"
          className="px-6 py-2 rounded-lg border border-green text-green hover:bg-green-50 transition"
          onClick={() => onCancel()}
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={isSubmitting}
          className="px-6 py-2 rounded-lg bg-green text-white hover:bg-green-600 transition disabled:opacity-50"
        >
          {isSubmitting ? 'Updating...' : 'Submit'}
        </button>
      </div>
    </form>
  );
};

export default ChangePassword;
